import type { CampingSpot } from '../types';
import type { RecentReview } from './firebaseService';

/**
 * Sammenslått NYHETER-feed: nye/bekreftede plasser og ferske anmeldelser i én
 * kronologisk strøm. Erstatter de tidligere «Nye steder»/«Anmeldelser»-fanene.
 * Ren logikk (ingen Firebase/React) så den kan enhetstestes.
 */

export type NewsFilter = 'all' | 'spots' | 'reviews';

export type NewsFeedItem =
  | { type: 'spot'; ts: number; id: string; spot: CampingSpot }
  | { type: 'review'; ts: number; id: string; review: RecentReview };

/**
 * Normaliser et tidsstempel til millisekunder. Håndterer tallet direkte,
 * ISO-streng, Firestore `Timestamp` (`toMillis()` / `{seconds}`) og `Date`.
 * Ukjent/manglende → 0 (havner nederst i feeden).
 */
export function toMs(raw: any): number {
  if (raw == null) return 0;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof raw === 'object') {
    if (raw instanceof Date) return raw.getTime();
    if (typeof raw.toMillis === 'function') return raw.toMillis();
    if (typeof raw.seconds === 'number') {
      return raw.seconds * 1000 + (typeof raw.nanoseconds === 'number' ? Math.floor(raw.nanoseconds / 1e6) : 0);
    }
  }
  return 0;
}

/** Nyhets-tidsstempel for en plass: nyeste av verifisert/opprettet. */
export function spotNewsTs(spot: CampingSpot): number {
  return toMs((spot as any).verifiedAt ?? spot.createdAt ?? 0);
}

/** Tidsstempel for en anmeldelse. */
export function reviewTs(review: RecentReview): number {
  return toMs(review.createdAt);
}

/**
 * Slå sammen plasser og anmeldelser til én liste sortert nyeste først.
 * Stabil: like tidsstempler bevarer relativ rekkefølge (plasser før anmeldelser
 * fordi de legges til først).
 */
export function mergeNewsFeed(spots: CampingSpot[], reviews: RecentReview[]): NewsFeedItem[] {
  const items: NewsFeedItem[] = [];
  for (const spot of spots ?? []) {
    if (!spot?.id) continue;
    items.push({ type: 'spot', ts: spotNewsTs(spot), id: spot.id, spot });
  }
  for (const review of reviews ?? []) {
    if (!review?.id) continue;
    items.push({ type: 'review', ts: reviewTs(review), id: review.id, review });
  }
  // Stabil descending sort (Array.prototype.sort er stabil i moderne JS-motorer).
  return items.sort((a, b) => b.ts - a.ts);
}

/**
 * «Kommune, fylke» fra denormaliserte spot-felter (umiddelbart, ingen henting).
 * Tom streng om ingen felter finnes → kalleren kan falle tilbake til reverse-
 * geokodet adresse eller koordinater. Brukes av NYHETER-feed-kortene.
 */
export function spotPlaceLabel(spot?: Pick<CampingSpot, 'municipality' | 'county'> | null): string {
  return spot ? [spot.municipality, spot.county].filter(Boolean).join(', ') : '';
}

/**
 * Er NYHETER-feeden faktisk synlig for brukeren nå? Brukes for å avgjøre når
 * uleste-badgen skal nullstilles («markert som sett»).
 *
 * Feeden vises i to ulike layouter:
 *  - Desktop: utvidet sidebar (`sidebarExpanded`).
 *  - Mobil: full-liste-visning (`mobileView === 'list'`) — `sidebarExpanded`
 *    er ALLTID false her, så en ren `sidebarExpanded`-sjekk gjør at badgen
 *    aldri nullstilles på mobil.
 */
export function isNewsFeedVisible(
  sidebarMode: string,
  sidebarExpanded: boolean,
  mobileView: 'map' | 'list',
): boolean {
  return sidebarMode === 'news' && (sidebarExpanded || mobileView === 'list');
}

/**
 * Denormaliserte skaper-/bekrefter-navn er språk-snapshots tatt da plassen ble
 * laget. Anonyme brukere (Apple privat-relay o.l.) får en av disse plassholderne
 * i stedet for et ekte navn — vi kjenner dem igjen på tvers av alle app-språk slik
 * at feed-avataren kan vise et nøytralt person-ikon i stedet for meningsløse
 * initialer som «UB». Match er case-insensitiv; hold i synk med translations/*.json
 * (`unknown_user`/`anonymous`).
 */
export const ANONYMOUS_CREATOR_NAMES = new Set([
  // anonymous
  'anonym', 'anonymous', 'anónimo', 'anonyme', 'anoniem', 'anonimowy',
  // unknown_user
  'ukjent bruker', 'unbekannter benutzer', 'unknown user', 'usuario desconocido',
  'utilisateur inconnu', 'onbekende gebruiker', 'nieznany użytkownik', 'okänd användare',
]);

/**
 * Har skaperen/bekrefteren en ekte, visbar identitet? Tomt eller et anonym-
 * plassholdernavn ⇒ false (kalleren viser da et generisk person-ikon).
 */
export function hasRealCreatorName(name?: string | null): boolean {
  const src = (name || '').trim();
  if (!src) return false;
  return !ANONYMOUS_CREATOR_NAMES.has(src.toLowerCase());
}

/**
 * Initialer for feed-avataren: navn → to bokstaver (ett ord → første to, flere ord
 * → første bokstav i første + siste ord). Antar et ekte navn (se
 * `hasRealCreatorName`); tomt navn gir tom streng.
 */
export function creatorInitials(name?: string | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Filtrer den sammenslåtte feeden på type. */
export function filterNewsFeed(items: NewsFeedItem[], filter: NewsFilter): NewsFeedItem[] {
  if (filter === 'spots') return items.filter((i) => i.type === 'spot');
  if (filter === 'reviews') return items.filter((i) => i.type === 'review');
  return items;
}
